/**
 * Два оформления, переключаемые на ходу.
 *
 * «Лаборатория» — интерфейс как геммологический отчёт: серифный заголовок,
 * моноширинные числа, штампы источника. «Прибор» — полевой инструмент:
 * панели, кнопки с физическим ребром, ЖК-дисплей для цифр, один оранжевый.
 *
 * Оформление — атрибут на <html>, вся разница живёт в styles.css. Выбор
 * хранится на устройстве: у одного человека на телефоне может стоять одно,
 * на айпаде другое.
 */
import { setting, setSetting } from '../db';

export type Theme = 'lab' | 'instrument' | 'sapphire';

/** Порядок, в котором темы сменяют друг друга по двойному касанию. */
export const THEMES: Theme[] = ['lab', 'instrument', 'sapphire'];

export const THEME_NAME: Record<Theme, string> = { lab: 'Лаборатория', instrument: 'Прибор', sapphire: 'Сапфир' };

export function nextTheme(t: Theme): Theme {
  return THEMES[(THEMES.indexOf(t) + 1) % THEMES.length];
}

const KEY = 'theme';
const EVENT = 'gn-theme';
let current: Theme = 'lab';

export function currentTheme(): Theme { return current; }

let fading: number | undefined;

export function applyTheme(t: Theme) {
  const changed = current !== t && document.documentElement.dataset.theme !== undefined;
  current = t;
  const root = document.documentElement;
  /*
   * Смена на глазах — плавная: цвета, рамки и тени перетекают за треть
   * секунды, а не щёлкают. Класс живёт только на время перехода, иначе
   * каждое обычное нажатие тоже начнёт «плыть».
   */
  if (changed) {
    root.classList.add('theming');
    window.clearTimeout(fading);
    fading = window.setTimeout(() => root.classList.remove('theming'), 420);
  }
  root.dataset.theme = t;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', t === 'instrument' ? '#E6E7EA' : t === 'sapphire' ? '#071A48' : '#F4F5F7');
  window.dispatchEvent(new CustomEvent(EVENT, { detail: t }));
}

export async function loadTheme(): Promise<Theme> {
  const t = await setting<Theme>(KEY, 'lab');
  applyTheme(THEMES.includes(t) ? t : 'lab');
  return current;
}

export async function setTheme(t: Theme) {
  applyTheme(t);
  await setSetting(KEY, t);
}

export function toggleTheme() {
  return setTheme(nextTheme(current));
}

export function onThemeChange(fn: (t: Theme) => void): () => void {
  const h = (e: Event) => fn((e as CustomEvent<Theme>).detail);
  window.addEventListener(EVENT, h);
  return () => window.removeEventListener(EVENT, h);
}

/**
 * Двойное касание по заголовку — быстрый способ показать оба оформления
 * рядом, не уходя в настройки. Одиночное касание ничего не делает, так что
 * случайно не переключится.
 */
export function doubleTap(fn: () => void, gapMs = 320) {
  let last = 0;
  return () => {
    const now = Date.now();
    if (now - last < gapMs) { last = 0; fn(); } else { last = now; }
  };
}
